import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { urlsShareBaseOrigin, urlToDomain } from '../../lib/utils/domains';
import { UUID, NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex } from '../../State/EvidenceDB/DatabaseTypes';
import { isDbNetworkEventWithSearchResults, ToSearchWorkerUpdateMessage, type FromSearchWorkerNewResultsMessage, type ToSearchWorkerStartMessage } from '../../ServiceWorker/NetworkEventDbSearchWebWorker';
import { mapGetOrCreate, toMap } from '../../lib/utils/mapGetOrCreate';
import { getDatabase } from '../../State/EvidenceDB/getDatabase';
import { HasDestinationUrlField, HasTab, NetworkEventDebuggerWebSocketCreated, NetworkEventOnBeforeRequest } from '../../lib/Events';
import { NewEventAddedToDatabase } from '../../State/StateEvents';
import { executeEventually } from '../../lib/utils/executeEventually';
import type { SearchResultEvent } from '../../lib/DbEventSearcher';
import type { DbNetworkEventWithSearchResults } from '../../lib/searchRecordedEvent';


export const splitContentSearchResultsByDestination = <T extends HasDestinationUrlField & HasTab>(resultsWithMixedDestinations: Iterable<T>) => {
	const sameRegisteredDomain: {[domain: string]: T[]} = {};
	const thirdPartyDomain: {[domain: string]: T[]} = {};
	for (const result of resultsWithMixedDestinations) {
		if (urlsShareBaseOrigin(result.destination.url, result.tab.url)) {
			(sameRegisteredDomain[urlToDomain(result.destination.url) ?? ""] ??= []).push(result);
		} else {
			(thirdPartyDomain[urlToDomain(result.destination.url) ?? ""] ??= []).push(result);
		}
	}
	return { sameRegisteredDomain: Object.entries(sameRegisteredDomain), thirdPartyDomain: Object.entries(thirdPartyDomain) };
}
export type SearchResultsDestinationTypeThenDomain<T extends HasDestinationUrlField & HasTab> = ReturnType<typeof splitContentSearchResultsByDestination<T>>;

export const splitResultsBySearchTermAndDestination = (mixedResults: SearchResultEvent[]) => {
	const resultsBySearchTerm /* : [string, ReturnType<typeof splitContentSearchResultsByDestination>][] */ = mixedResults
		.reduce( (bySearchTerm, searchEvent) => {
			if (isDbNetworkEventWithSearchResults(searchEvent)) {
				const { searchResults } = searchEvent;
				const uniqueSearchTermsFound = [...new Set<string>(searchResults.map( r => r.originalSearchTerm ))];
				for (const searchTerm of uniqueSearchTermsFound) {
					const filteredSearchResults = searchResults.filter( r => r.originalSearchTerm === searchTerm );
					(bySearchTerm[searchTerm] ??= []).push({...searchEvent, searchResults: filteredSearchResults});
				}
			}
			return bySearchTerm;
		}, {} as {[searchTerm: string]: DbNetworkEventWithSearchResults[]})
	const resultsBySearchTermAndDestination: [string, ReturnType<typeof splitContentSearchResultsByDestination<DbNetworkEventWithSearchResults>>][] =
		Object.entries(resultsBySearchTerm)
			.map( ([searchTerm, results]) =>
				([searchTerm, splitContentSearchResultsByDestination(results)] as [string, ReturnType<typeof splitContentSearchResultsByDestination<DbNetworkEventWithSearchResults>>])
			);
	return resultsBySearchTermAndDestination;
}


export interface SearchTermWithResults {
	searchTerm: string;
	searchResultsDestinationSharesSameRegisteredDomain: [string, SearchResultEvent[]][];
	searchResultsDestinationDomain3rdParty: [string, SearchResultEvent[]][];
}

const searchTermsToKey = (searchTerms: string[]) => searchTerms.sort().join("\0");

export class DbRecordedEventSearchState {
	#searchWorker: Worker | undefined;
	#stale = false;
	#previousSearchTermsKey: string = "";

	private _searchTerms: string[] = [];
	private _results = [] as SearchResultEvent[];
	private _eventsProcessed: number = 0;
	private _ofTotalEvents: number = 0;
	private _charactersProcessed: number = 0;
	private _connectionsProcessed: number = 0;
	private _connections: number = 0;

	@computed get searchTerms() {
		return this._searchTerms;
	}
	@computed get results() {
		return this._results;
	}
	@computed get eventsProcessed() {
		return this._eventsProcessed;
	}
	@computed get ofTotalEvents() {
		return this._ofTotalEvents;
	}
	@computed get charactersProcessed() {
		return this._charactersProcessed;
	}
	@computed get connectionsProcessed() {
		return this._connectionsProcessed;
	}
	@computed get connections() {	
		return this._connections;
	}

	constructor(
		private recordingUuid: UUID,
		private workerUrl: string
	) {
		makeObservable<this, "_searchTerms" | "_results" | "_eventsProcessed" | "_ofTotalEvents" | "_charactersProcessed" | "_connectionsProcessed" | "_connections">(this, {
			_searchTerms: observable,
			_results: observable,
			_eventsProcessed: observable,
			_ofTotalEvents: observable,
			_charactersProcessed: observable,
			_connectionsProcessed: observable,
			_connections: observable,
		});
		this.#updateConnectionCount();
	}

	protected onMessageFromWorker = action ( (e: MessageEvent<any>) => {
		const message = e.data as FromSearchWorkerNewResultsMessage;
		if (message.type === 'search-results') {
			this._eventsProcessed = message.eventsProcessed;
			this._ofTotalEvents = message.ofTotalEvents;
			this._connectionsProcessed = message.connectionsProcessed;
			this._charactersProcessed = message.charactersProcessed;
			if (this.#stale) {
				this._results = [];
				this.#stale = false;
			}
			this._results.push(...message.eventsFound);
		}
	});

	setSearchTerms = action( (searchTerms: string[]) => {
		const searchTermsKey = searchTermsToKey(searchTerms);
		if (this.#previousSearchTermsKey !== searchTermsKey || this.#searchWorker == null) {
			this._searchTerms = searchTerms;
			this.#previousSearchTermsKey = searchTermsKey;
			this.#searchWorker?.terminate();
			this.#stale = true;
			this.#searchWorker = new Worker(this.workerUrl, {
				type: 'module'
			});
			this.#searchWorker.addEventListener("message", this.onMessageFromWorker);				
			this.#searchWorker.postMessage({
				type: 'start',
				recordingUuid: this.recordingUuid,
				searchTerms,
				searchDataReceived: true,
			} satisfies ToSearchWorkerStartMessage);
		}
	})

	#updateConnectionCount = async () => {
		const db = await getDatabase();
		if (db == null) return;
		const webSocketCreatedBaseBound = [
			this.recordingUuid, 
			'Debugger.Network' satisfies NetworkEventDebuggerWebSocketCreated["category"],
			"Network.webSocketCreated" satisfies NetworkEventDebuggerWebSocketCreated["eventName"]
		] as const;
		const webSocketBound = IDBKeyRange.bound([...webSocketCreatedBaseBound, 0], [...webSocketCreatedBaseBound, Number.MAX_SAFE_INTEGER]);
		const httpConnectCountBaseBound = [
			this.recordingUuid, 
			"WebRequest" satisfies NetworkEventOnBeforeRequest["category"],
			"WebRequest.beforeRequest" satisfies NetworkEventOnBeforeRequest["eventName"]
		] as const;
		const httpConnectCountBound = IDBKeyRange.bound([...httpConnectCountBaseBound, 0], [...httpConnectCountBaseBound, Number.MAX_SAFE_INTEGER]);
		const [webSocketConnectionCount, httpConnectionCount] = await Promise.all([
			db.countFromIndex('events', NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, webSocketBound),
			db.countFromIndex('events', NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, httpConnectCountBound)
		]);
		// ProcessInfo.log(`Connection counts for recording ${this.recordingUuid}: ${httpConnectionCount} HTTP, ${webSocketConnectionCount} WebSocket`);
		runInAction(() => {
			this._connections = httpConnectionCount + webSocketConnectionCount;
		});
	}

	/**
	 * Ask the search worker to update its results for new events recorded 
	 **/
	update = executeEventually( async () => {
		// Update the connection count to reflect the new events recorded.
		await this.#updateConnectionCount();
		this.#searchWorker?.postMessage({
			recordingUuid: this.recordingUuid,
			type: 'update',
		} satisfies ToSearchWorkerUpdateMessage);
	});

	static #forRecordingUuid = new Map<UUID, DbRecordedEventSearchState>();

	static forRecordingUuid = (recordingUuid: UUID) => 
		DbRecordedEventSearchState.#forRecordingUuid.get(recordingUuid);

	static forRecordingUuidAndSearchTerms = (recordingUuid: UUID, searchTerms: string[], workerUrl: string) => {
		const eventSearchState = mapGetOrCreate(DbRecordedEventSearchState.#forRecordingUuid, recordingUuid, () => new DbRecordedEventSearchState(recordingUuid, workerUrl));
		eventSearchState.setSearchTerms(searchTerms);
		return eventSearchState;
	}
}

NewEventAddedToDatabase.addListener( async ({recordingUuid}) => {
		await DbRecordedEventSearchState.forRecordingUuid(recordingUuid)?.update();
	}
);